import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'
import { match, P } from 'ts-pattern'

import { Duration } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { NumberUtil } from '@root/shared'

import type { TradeSettings } from '@root/entities/trade-settings'

export type EvmValues = {
  gasPrice: string
  flexibleGasPrice: boolean
  maxGasPrice: string
  maxPriorityFeePerGas: string
}

export type SvmValues = {
  jitoEnabled: boolean
  jitoTip: string
  priorityFee: string
  autoFee: boolean
  maxAutoPriority: string
}

export type CommonValues = {
  slippage: string
  slippageAuto: boolean
  deadline: string
  antiMev: boolean
}

export type TradeSettingsValues = EvmValues & SvmValues & CommonValues

export const createDefaultValues = (): TradeSettingsValues => {
  return {
    antiMev: true,
    deadline: '60',
    jitoEnabled: true,
    gasPrice: '0',
    jitoTip: '0.0002',
    priorityFee: '0.0001',
    slippage: '5',
    maxGasPrice: '0',
    maxPriorityFeePerGas: '0',
    flexibleGasPrice: false,
    autoFee: false,
    slippageAuto: false,
    maxAutoPriority: '',
  }
}

export const formValuesFromSettings = (
  settings: TradeSettings.TradeSettings,
): TradeSettingsValues => {
  return match(settings)
    .returnType<TradeSettingsValues>()
    .with({ network: Network.Solana }, (settings) => {
      return {
        antiMev: settings.jitoEnabled,
        deadline: Duration.toMinutes(settings.deadline).toString(),
        jitoEnabled: settings.jitoEnabled,
        jitoTip: settings.jitoTip.toString(),
        priorityFee: settings.priorityFee.toString(),
        slippage: settings.slippage.toString(),
        gasPrice: '0',
        maxGasPrice: '',
        maxPriorityFeePerGas: '',
        flexibleGasPrice: false,
        autoFee: false,
        slippageAuto: false,
        maxAutoPriority: '',
      }
    })
    .with({ network: Network.Ton }, (settings) => {
      return {
        antiMev: true,
        deadline: Duration.toMinutes(settings.deadline).toString(),
        jitoEnabled: true,
        jitoTip: '0',
        priorityFee: '0',
        slippage: settings.slippage.toString(),
        gasPrice: settings.gasPrice.toFormat(),
        maxGasPrice: '',
        maxPriorityFeePerGas: '',
        flexibleGasPrice: false,
        autoFee: false,
        slippageAuto: false,
        maxAutoPriority: '',
      }
    })
    .with(
      { network: P.union(Network.Ethereum, Network.Base, Network.Bsc) },
      (settings) => {
        return {
          antiMev: settings.antiMevEnabled,
          deadline: Duration.toMinutes(settings.deadline).toString(),
          jitoEnabled: true,
          jitoTip: '0',
          priorityFee: '0',
          slippage: settings.slippage.toString(),
          gasPrice: settings.gasPrice?.toFormat() ?? '',
          maxGasPrice: settings.maxGasPrice?.toFormat() ?? '',
          maxPriorityFeePerGas: settings.maxPriorityFeePerGas?.toFormat() ?? '',
          flexibleGasPrice: settings.gasPrice !== undefined,
          autoFee: false,
          slippageAuto: false,
          maxAutoPriority: '',
        }
      },
    )
    .exhaustive()
}

export const tradeSettingsFromValues = (
  network: Network.Network,
  values: TradeSettingsValues,
): TradeSettings.TradeSettings => {
  const deadlineMinutes =
    NumberUtil.parseBigNumber(values.deadline) || BigNumber(0)
  const deadlineMs = DateF.minutesToMilliseconds(deadlineMinutes.toNumber())

  const tradeSettings = match(network)
    .returnType<TradeSettings.TradeSettings>()
    .with(P.union(Network.Ethereum, Network.Base, Network.Bsc), (network) => {
      return {
        network,
        antiMevEnabled: values.antiMev,
        deadline: Duration.millis(deadlineMs),
        slippage: Number(values.slippage),
        gasPrice: BigNumber(values.gasPrice),
        maxGasPrice: BigNumber(values.maxGasPrice),
        maxPriorityFeePerGas: BigNumber(values.maxPriorityFeePerGas),
      }
    })
    .with(Network.Ton, () => {
      return {
        network: Network.Ton,
        deadline: Duration.millis(deadlineMs),
        slippage: Number(values.slippage),
        gasPrice: BigNumber(values.gasPrice),
      }
    })
    .with(Network.Solana, () => {
      return {
        network: Network.Solana,
        deadline: Duration.millis(deadlineMs),
        slippage: Number(values.slippage),
        jitoEnabled: values.jitoEnabled,
        jitoTip: Number(values.jitoTip),
        priorityFee: Number(values.priorityFee),
      }
    })

    .exhaustive()

  return tradeSettings
}
